/**
 * Session Manager Utility
 * Handles session expiration detection and state preservation during booking flow
 */

const BOOKING_STATE_KEY = 'preserved_booking_state';
const REDIRECT_AFTER_LOGIN_KEY = 'redirectAfterLogin';

/**
 * Get current user ID from sessionStorage
 * @returns {string} - User ID or 'guest'
 */
const getCurrentUserId = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    return user.id || 'guest';
  } catch {
    return 'guest';
  }
};

/**
 * Save current booking state to sessionStorage (user-specific) - more persistent than sessionStorage
 * @param {Object} bookingState - Current booking state to preserve
 * @antml:parameter name="currentPath">Current page path
 */
export const preserveBookingState = (bookingState, currentPath) => {
  try {
    const userId = getCurrentUserId();
    const stateToPreserve = {
      ...bookingState,
      preservedAt: Date.now(),
      returnPath: currentPath,
      userId // Store userId to verify on retrieval
    };
    
    const userSpecificKey = `${BOOKING_STATE_KEY}_${userId}`;
    // Use sessionStorage instead of sessionStorage for better persistence
    sessionStorage.setItem(userSpecificKey, JSON.stringify(stateToPreserve));
    sessionStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, currentPath);
    
    console.log('✅ Booking state preserved for user:', userId, {
      path: currentPath,
      hasPassengers: !!bookingState.passengerData,
      passengerCount: bookingState.passengerData?.length
    });
  } catch (error) {
    console.error('❌ Failed to preserve booking state:', error);
  }
};

/**
 * Retrieve preserved booking state from sessionStorage (user-specific)
 * @returns {Object|null} - Preserved booking state or null
 */
export const getPreservedBookingState = () => {
  try {
    const userId = getCurrentUserId();
    const userSpecificKey = `${BOOKING_STATE_KEY}_${userId}`;
    
    console.log('🔍 getPreservedBookingState: Looking for key:', userSpecificKey);
    console.log('🔍 Current userId:', userId);
    
    // Debug: List all preserved_booking_state keys in sessionStorage
    const allKeys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('preserved_booking_state')) {
        allKeys.push(key);
      }
    }
    console.log('🔍 All preserved booking state keys in sessionStorage:', allKeys);
    
    const preserved = sessionStorage.getItem(userSpecificKey);
    
    if (!preserved) {
      console.log('❌ No preserved state found for key:', userSpecificKey);
      return null;
    }
    
    console.log('✅ Found preserved state:', preserved);
    const state = JSON.parse(preserved);
    
    // Verify the state belongs to the current user
    if (state.userId && state.userId !== userId) {
      console.warn('⚠️ Preserved booking state belongs to different user, clearing...');
      clearPreservedBookingState();
      return null;
    }
    
    // Check if state is still valid (within 30 minutes)
    const thirtyMinutes = 30 * 60 * 1000;
    if (Date.now() - state.preservedAt > thirtyMinutes) {
      console.warn('⚠️ Preserved booking state expired');
      clearPreservedBookingState();
      return null;
    }
    
    console.log('✅ Retrieved preserved booking state for user:', userId, {
      path: state.returnPath,
      age: Math.floor((Date.now() - state.preservedAt) / 1000) + 's'
    });
    
    return state;
  } catch (error) {
    console.error('❌ Failed to retrieve preserved booking state:', error);
    return null;
  }
};

/**
 * Clear preserved booking state from sessionStorage (user-specific)
 */
export const clearPreservedBookingState = () => {
  try {
    const userId = getCurrentUserId();
    const userSpecificKey = `${BOOKING_STATE_KEY}_${userId}`;
    sessionStorage.removeItem(userSpecificKey);
    console.log('🧹 Cleared preserved booking state for user:', userId);
  } catch (error) {
    console.error('❌ Failed to clear preserved booking state:', error);
  }
};

/**
 * Check if there's a preserved booking state (user-specific)
 * @returns {boolean}
 */
export const hasPreservedBookingState = () => {
  const userId = getCurrentUserId();
  const userSpecificKey = `${BOOKING_STATE_KEY}_${userId}`;
  return !!sessionStorage.getItem(userSpecificKey);
};

/**
 * Get the return path from preserved state
 * @returns {string|null}
 */
export const getPreservedReturnPath = () => {
  const state = getPreservedBookingState();
  return state?.returnPath || null;
};
